/**
 * Client-side photos API module — replaces photos/actions.ts Server Actions.
 */

import { PHOTOS } from "@/shared/constants/backend-api"
import { authFetch, authFetchJson } from "@/shared/lib/auth-fetch"
import { ApiResponse } from "@/shared/types/backend-response.type"
import { buildUrl } from "@/shared/lib/utils"

// Re-export types originally defined in the action file
export interface Photo {
  id: number
  homeId: number
  imageUrl: string
  thumbnailUrl: string
  mediaType: "image" | "video" | "document"
  takenAt: string | null
  uploadedAt: string
  workOrderId?: number | null
  description?: string | null
  tags?: string[] | null
  latitude?: number | null
  longitude?: number | null
  duration?: number | null
  videoCodec?: string | null
  videoResolution?: string | null
  fileSize?: number | null
  isDeleted: boolean
  createdAt: string
  updatedAt: string
  uploadedByUserId?: number | null
  uploadedByUser?: { id: number; fullname: string } | null
  workScopeComplete?: WorkScopeCompleteSummary | null
}

export type WorkScopePhotoSlot = "before" | "after"

export interface WorkScopeCompleteSummary {
  workScopeId: number
  workScopeName: string
  taggedByUserId: number | null
  slot: WorkScopePhotoSlot
}

export interface WorkScopeCompletePhotoEntry {
  photoId: number
  thumbnailUrl: string
  takenAt: string | null
  taggedByUserId: number | null
  tagger: { userId: number; fullname: string | null } | null
}

/** After picture fields stay at the top level. `before` is null when unset. */
export interface WorkScopeCompletePhotosForScope {
  photoId: number | null
  thumbnailUrl: string | null
  takenAt: string | null
  taggedByUserId: number | null
  tagger: { userId: number; fullname: string | null } | null
  before: WorkScopeCompletePhotoEntry | null
}

export interface WorkScopeCompleteOption {
  workScopeId: number
  workScopeName: string
  status: string | null
  completePhotoId: number | null
  beforePhotoId: number | null
}

export interface WorkScopeSlugRecord {
  id: number
  workScope: string
  workScopeDisplay: string | null
}

export interface PhotoFilterOptions {
  workOrderId?: number
  tags?: string[]
  dateFrom?: string
  dateTo?: string
  includeDeleted?: boolean
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const PHOTO_QUERY_KEYS = {
  all: ["photos"] as const,
  byHome: (homeId: number) => ["photos", "home", homeId] as const,
  detail: (photoId: number) => ["photos", "detail", photoId] as const,
  stats: (homeId: number) => ["photos", "stats", homeId] as const,
  workScopeComplete: (homeId: number) => ["photos", "work-scope-complete", homeId] as const,
  workScopeOptions: (homeId: number) => ["photos", "work-scope-options", homeId] as const,
  workScopeCatalog: ["work-scopes", "slug-catalog"] as const,
}

// ---------------------------------------------------------------------------
// Read
// ---------------------------------------------------------------------------

export async function getPhotosByHomeId(
  homeId: number,
  filters?: PhotoFilterOptions,
): Promise<ApiResponse<Photo[]>> {
  const params: Record<string, string> = {}
  if (filters?.workOrderId) params.workOrderId = filters.workOrderId.toString()
  if (filters?.tags?.length) params.tags = filters.tags.join(",")
  if (filters?.dateFrom) params.dateFrom = filters.dateFrom
  if (filters?.dateTo) params.dateTo = filters.dateTo
  if (filters?.includeDeleted) params.includeDeleted = "true"

  const url = buildUrl(PHOTOS.byHome(homeId), params)
  return authFetchJson<Photo[]>(url)
}

export async function getPhotoById(photoId: number): Promise<ApiResponse<Photo>> {
  return authFetchJson<Photo>(PHOTOS.byId(photoId))
}

export async function getPhotoStats(
  homeId: number,
): Promise<ApiResponse<{
  totalPhotos: number
  photosByWorkOrder: Record<number, number>
  photosByMonth: Record<string, number>
}>> {
  return authFetchJson(PHOTOS.stats(homeId))
}

// ---------------------------------------------------------------------------
// Upload
// ---------------------------------------------------------------------------

export async function uploadMultiplePhotos(
  homeId: number,
  files: File[],
  workOrderId?: number,
  description?: string,
  tags?: string[],
): Promise<ApiResponse<Photo[]>> {
  if (files.length === 0) return { success: false, message: "At least one photo is required", status: 400 }
  if (files.length > 30) return { success: false, message: "Maximum 30 photos can be uploaded at once", status: 400 }

  // Validate unsupported formats
  const unsupportedExts = [".avi", ".mkv", ".flv", ".wmv", ".3gp", ".divx", ".xvid"]
  const unsupportedMimes = ["video/x-msvideo", "video/x-matroska", "video/x-flv", "video/x-ms-wmv", "video/3gpp"]
  for (const file of files) {
    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf("."))
    if (unsupportedExts.includes(ext) || unsupportedMimes.includes(file.type.toLowerCase())) {
      return {
        success: false,
        message: `Video "${file.name}" format is not supported. Please convert to MP4, MOV, WebM, or M4V.`,
        status: 400,
      }
    }
  }

  const formData = new FormData()
  files.forEach((file) => formData.append("photos", file))
  formData.append("homeId", homeId.toString())
  if (workOrderId !== undefined) formData.append("workOrderId", workOrderId.toString())
  if (description) formData.append("description", description)
  if (tags?.length) formData.append("tags", JSON.stringify(tags))

  const response = await authFetch(PHOTOS.uploadMultiple, {
    method: "POST",
    body: formData,
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    return { success: false, message: (errorData as any).message || `HTTP ${response.status}`, status: response.status }
  }

  const data = await response.json()
  return { success: true, data: data.data || data }
}

// ---------------------------------------------------------------------------
// Update / Delete
// ---------------------------------------------------------------------------

export async function updatePhoto(
  photoId: number,
  updateData: { description?: string; tags?: string[]; takenAt?: string | null; isDeleted?: boolean },
): Promise<ApiResponse<Photo>> {
  return authFetchJson<Photo>(PHOTOS.byId(photoId), {
    method: "PUT",
    body: JSON.stringify(updateData),
  })
}

export async function deletePhoto(photoId: number): Promise<ApiResponse<string>> {
  return authFetchJson<string>(PHOTOS.deletePermanent(photoId), { method: "DELETE" })
}

export async function getWorkScopeCompletePhotos(
  homeId: number,
): Promise<ApiResponse<Record<string, WorkScopeCompletePhotosForScope>>> {
  return authFetchJson<Record<string, WorkScopeCompletePhotosForScope>>(PHOTOS.workScopeComplete(homeId))
}

export async function getWorkScopeCompleteOptions(
  homeId: number,
): Promise<ApiResponse<WorkScopeCompleteOption[]>> {
  return authFetchJson<WorkScopeCompleteOption[]>(PHOTOS.workScopeOptions(homeId))
}

export async function getWorkScopeSlugCatalog(): Promise<ApiResponse<WorkScopeSlugRecord[]>> {
  return authFetchJson<WorkScopeSlugRecord[]>("/work-scopes/priority-table")
}

export async function getWorkScopeBySlug(
  slug: string,
): Promise<ApiResponse<WorkScopeSlugRecord | null>> {
  return authFetchJson<WorkScopeSlugRecord | null>(`/work-scopes/by-name?workScope=${encodeURIComponent(slug)}`)
}

export async function tagWorkScopeComplete(
  photoId: number,
  workScopeId: number,
  slot: WorkScopePhotoSlot,
): Promise<ApiResponse<{ photoId: number; workScopeId: number; slot: WorkScopePhotoSlot }>> {
  return authFetchJson(PHOTOS.workScopeCompleteTag(photoId), {
    method: "PUT",
    body: JSON.stringify({ workScopeId, slot }),
  })
}

export async function untagWorkScopeComplete(
  photoId: number,
): Promise<ApiResponse<{ photoId: number; workScopeId: number }>> {
  return authFetchJson(PHOTOS.workScopeCompleteTag(photoId), { method: "DELETE" })
}
